import { useCallback, useEffect, useState } from "react";
import { MdHistory, MdRestore } from "react-icons/md";
import TextDiff from "components/common/TextDiff";
import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  InfoBox,
  Modal,
  Spinner,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import {
  PolicyHistoryAction,
  PolicyHistoryEntry,
  guardrailsService,
} from "services/guardrailsService";
import { getThemeColor } from "theme/colors";

interface PolicyHistoryCardProps {
  /** Bumped by the page after a save, regenerate or refresh. Re-reads the history without remounting. */
  refreshToken: number;
  /** Puts a historical version's text into the policy editor. Nothing is saved until the user clicks Save. */
  onLoadVersion: (policyText: string) => void;
}

const ACTION_PALETTE: Record<PolicyHistoryAction, string> = {
  Created: "green",
  Updated: "blue",
  Deleted: "red",
};

const formatDateTime = (iso: string) => new Date(iso).toLocaleString();

/**
 * Change history of the company guardrails policy: every saved version, who saved it and when.
 * A row opens the full text of that version (and the text it replaced), and can load it back into
 * the editor so the admin can restore it with a normal save.
 */
const PolicyHistoryCard = ({
  refreshToken,
  onLoadVersion,
}: PolicyHistoryCardProps) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<PolicyHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<PolicyHistoryEntry | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setEntries(await guardrailsService.getPolicyHistory());
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory, refreshToken]);

  const actionLabel = (action: PolicyHistoryAction) =>
    t(`app.security.guardrailsPolicy.history.actions.${action}`);

  const actorLabel = (entry: PolicyHistoryEntry) =>
    entry.changedByEmail ??
    t("app.security.guardrailsPolicy.history.systemActor");

  const handleLoadVersion = () => {
    if (!selected) return;
    onLoadVersion(selected.companyPolicyText);
    setSelected(null);
  };

  const renderPolicyText = (text: string | null) => (
    <Box
      as="pre"
      p={3}
      borderRadius="md"
      border="1px solid"
      borderColor={{ base: "gray.200", _dark: "gray.700" }}
      bg={{ base: "gray.50", _dark: "gray.900" }}
      fontFamily="monospace"
      fontSize="sm"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
      maxH="320px"
      overflowY="auto"
    >
      {text?.trim()
        ? text
        : t("app.security.guardrailsPolicy.history.emptyText")}
    </Box>
  );

  const renderRow = (entry: PolicyHistoryEntry) => (
    <Table.Row key={entry.id}>
      <Table.Cell>
        <Text fontSize="sm">{formatDateTime(entry.changedAt)}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">{actorLabel(entry)}</Text>
      </Table.Cell>
      <Table.Cell>
        <Badge
          colorPalette={ACTION_PALETTE[entry.action] ?? "gray"}
          variant="subtle"
        >
          {actionLabel(entry.action)}
        </Badge>
      </Table.Cell>
      <Table.Cell>
        <Text fontSize="sm">v{entry.policyVersion}</Text>
      </Table.Cell>
      <Table.Cell textAlign="end">
        <Button intent="ghost" size="sm" onClick={() => setSelected(entry)}>
          {t("app.security.guardrailsPolicy.history.view")}
        </Button>
      </Table.Cell>
    </Table.Row>
  );

  const renderBody = () => {
    if (loading && entries.length === 0) return <Spinner size="sm" />;
    if (failed)
      return (
        <InfoBox variant="error">
          <Text fontSize="sm">
            {t("app.security.guardrailsPolicy.history.loadFailed")}
          </Text>
        </InfoBox>
      );
    if (entries.length === 0)
      return (
        <Text fontSize="sm" color={getThemeColor("text.secondary")}>
          {t("app.security.guardrailsPolicy.history.empty")}
        </Text>
      );

    return (
      <Box overflowX="auto">
        <Table.Root size="sm" variant="outline" minW="620px">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>
                {t("app.security.guardrailsPolicy.history.when")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.security.guardrailsPolicy.history.changedBy")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.security.guardrailsPolicy.history.action")}
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                {t("app.security.guardrailsPolicy.history.version")}
              </Table.ColumnHeader>
              <Table.ColumnHeader />
            </Table.Row>
          </Table.Header>
          <Table.Body>{entries.map(renderRow)}</Table.Body>
        </Table.Root>
      </Box>
    );
  };

  return (
    <>
      <Card>
        <Card.Body>
          <VStack align="stretch" gap={3}>
            <Box>
              <HStack gap={2} mb={1}>
                <MdHistory />
                <Heading size="sm">
                  {t("app.security.guardrailsPolicy.history.title")}
                </Heading>
              </HStack>
              <Text
                fontSize="xs"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.security.guardrailsPolicy.history.description")}
              </Text>
            </Box>
            {renderBody()}
          </VStack>
        </Card.Body>
      </Card>

      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        size="xl"
      >
        {selected && (
          <>
            <Modal.Header>
              <VStack align="start" gap={1}>
                <Heading size="md">
                  {t("app.security.guardrailsPolicy.history.dialogTitle", {
                    version: selected.policyVersion,
                  })}
                </Heading>
                <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                  {t("app.security.guardrailsPolicy.history.savedBy", {
                    action: actionLabel(selected.action),
                    date: formatDateTime(selected.changedAt),
                    actor: actorLabel(selected),
                  })}
                </Text>
              </VStack>
            </Modal.Header>
            <Modal.Body>
              <VStack align="stretch" gap={4}>
                {selected.previousPolicyText !== null && (
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={2}>
                      {t("app.security.guardrailsPolicy.history.changes")}
                    </Text>
                    <TextDiff
                      before={selected.previousPolicyText}
                      after={selected.companyPolicyText}
                    />
                  </Box>
                )}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    {selected.action === "Deleted"
                      ? t("app.security.guardrailsPolicy.history.clearedText")
                      : t("app.security.guardrailsPolicy.history.versionText")}
                  </Text>
                  {renderPolicyText(selected.companyPolicyText)}
                </Box>
              </VStack>
            </Modal.Body>
            <Modal.Footer>
              <Button intent="secondary" onClick={() => setSelected(null)}>
                {t("app.common.close")}
              </Button>
              <Button
                intent="primary"
                onClick={handleLoadVersion}
                disabled={!selected.companyPolicyText.trim()}
              >
                <MdRestore style={{ marginRight: "0.5rem" }} />
                {t("app.security.guardrailsPolicy.history.loadIntoEditor")}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default PolicyHistoryCard;
